import React, { useState } from 'react';
import { 
  FaBriefcase, 
  FaBuilding, 
  FaSearch, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaArrowRight, 
  FaBookOpen, 
  FaUserTie, 
  FaAward, 
  FaChevronRight,
  FaShieldAlt,
  FaNetworkWired
} from 'react-icons/fa';

// Mock recommended jobs targeting companies (tbl_entity)
const MOCK_JOBS = [
  {
    id: "job_01",
    title: "Senior Database Infrastructure Architect",
    company: "Prisma Cloud Technologies",
    handle: "prismacloud",
    location: "San Francisco, CA (Hybrid)",
    salary: "$160k - $190k",
    posted: "1 day ago",
    matchPercentage: 92,
    requirements: {
      domains: ["Database Architecture", "Backend Systems"],
      tools: ["PostgreSQL", "Knex.js", "AWS Lambda"],
      skills: ["Database Migrations", "Schema Validation"],
      soft_skills: ["Technical Architecture"],
      certificates: ["AWS Certified Cloud Practitioner"]
    }
  },
  {
    id: "job_02",
    title: "Lead AI Integration Engineer",
    company: "Vortex Labs",
    handle: "vortex_labs",
    location: "Remote (US)",
    salary: "$145k - $175k",
    posted: "3 days ago",
    matchPercentage: 68,
    requirements: {
      domains: ["Backend Systems", "Data Pipelines"],
      tools: ["Gemini API", "PostgreSQL", "Docker"],
      skills: ["LLM Structured Extraction", "Vector Embeddings"],
      soft_skills: ["Rapid Execution"],
      certificates: []
    }
  }
];

// Mock user's active verified skill ledger state for gap computation
const USER_VERIFIED_LEDGER = {
  domains: ["Backend Systems", "Database Architecture"],
  tools: ["PostgreSQL", "AWS Lambda"], // Missing: Knex.js, Gemini API, Docker
  skills: ["Database Migrations", "Schema Validation", "Vector Embeddings"], // Missing: LLM Structured Extraction
  soft_skills: ["Technical Architecture", "Rapid Execution"],
  certificates: ["AWS Certified Cloud Practitioner"]
};

// Mock employee nodes matching companies (tbl_entity) for referral network
const MOCK_INSIDER_NETWORK = {
  prismacloud: [
    { name: "Sarah Jenkins", handle: "sjenkins", role: "Database Engineer II", avatar: "SJ", mutuals: 4 },
    { name: "Marcus Sterling", handle: "msterling", role: "DevOps Lead", avatar: "MS", mutuals: 1 }
  ],
  vortex_labs: [
    { name: "Alex Rivera", handle: "arivera", role: "Data Scientist", avatar: "AR", mutuals: 2 }
  ]
};

export default function JobsCenter({ onGenerateCourse }) {
  const [selectedJob, setSelectedJob] = useState(MOCK_JOBS[0]);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredJobs = MOCK_JOBS.filter(job => 
    job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.company.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const calculateMatchGaps = (job) => {
    const gaps = [];
    
    // Check missing domains
    job.requirements.domains.forEach(d => {
      if (!USER_VERIFIED_LEDGER.domains.includes(d)) gaps.push({ type: "Domain", name: d, missing: true });
    });
    
    // Check missing tools
    job.requirements.tools.forEach(t => {
      if (!USER_VERIFIED_LEDGER.tools.includes(t)) gaps.push({ type: "Tool", name: t, missing: true });
      else gaps.push({ type: "Tool", name: t, missing: false });
    });

    // Check missing skills
    job.requirements.skills.forEach(s => {
      if (!USER_VERIFIED_LEDGER.skills.includes(s)) gaps.push({ type: "Skill", name: s, missing: true });
      else gaps.push({ type: "Skill", name: s, missing: false });
    });

    // Check missing certificates
    job.requirements.certificates.forEach(c => {
      if (!USER_VERIFIED_LEDGER.certificates.includes(c)) gaps.push({ type: "Certificate", name: c, missing: true });
      else gaps.push({ type: "Certificate", name: c, missing: false });
    });

    return gaps;
  };

  const activeGaps = calculateMatchGaps(selectedJob);
  const insiders = MOCK_INSIDER_NETWORK[selectedJob.handle] || [];

  const handleStartSyllabus = (skillName) => {
    if (onGenerateCourse) {
      onGenerateCourse(skillName);
    } else {
      alert(`Initializing automated syllabus generator for missing element: ${skillName}`);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.jobsLayout}>
        
        {/* Left Column: Recommended Open Roles */}
        <div style={styles.jobsListPanel}>
          <div style={styles.searchHeader}>
            <div style={styles.searchBox}>
              <FaSearch style={styles.searchIcon} />
              <input 
                type="text" 
                placeholder="Search matching roles, technologies..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={styles.jobSearchInput}
              />
            </div>
          </div>

          <div style={styles.cardsFeed}>
            {filteredJobs.map((job) => {
              const isActive = selectedJob.id === job.id;
              return (
                <div 
                  key={job.id} 
                  onClick={() => setSelectedJob(job)}
                  style={{ 
                    ...styles.jobItemCard, 
                    ...(isActive ? styles.activeJobCard : {}) 
                  }}
                >
                  <div style={styles.cardHeader}>
                    <div style={styles.companyIconPlaceholder}>
                      <FaBuilding />
                    </div>
                    <div style={styles.jobMainMeta}>
                      <h4 style={styles.jobTitle}>{job.title}</h4>
                      <span style={styles.companyName}>{job.company}</span>
                    </div>
                    <div style={{
                      ...styles.matchPill,
                      backgroundColor: job.matchPercentage >= 90 ? 'rgba(35, 134, 54, 0.1)' : 'rgba(210, 153, 34, 0.1)',
                      color: job.matchPercentage >= 90 ? '#39d353' : '#d29922'
                    }}>
                      {job.matchPercentage}% fit
                    </div>
                  </div>

                  <div style={styles.cardSubDetails}>
                    <span style={styles.subDetailText}>{job.location}</span>
                    <span style={styles.dotDivider}>•</span>
                    <span style={styles.subDetailText}>{job.salary}</span>
                    <span style={styles.dotDivider}>•</span>
                    <span style={styles.timeBadge}>{job.posted}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Column: In-Depth Real-time Gap Analyzer Dashboard */}
        <div style={styles.gapAnalyzerPanel}>
          <div style={styles.panelTitleHeader}>
            <FaShieldAlt style={styles.analyzerIcon} />
            <h3 style={styles.analyzerHeading}>Live-Resume Match Analysis</h3>
          </div>

          <div style={styles.targetJobCard}>
            <span style={styles.analyzerTargetCompany}>{selectedJob.company}</span>
            <h2 style={styles.analyzerTargetTitle}>{selectedJob.title}</h2>
          </div>

          {/* Real-time Gap breakdown */}
          <div style={styles.analyzerSection}>
            <h4 style={styles.sectionHeading}>Taxonomy Gap Auditing</h4>
            <p style={styles.sectionDesc}>Based on your active verification blocks across Skills, Tools, and Certifications:</p>
            
            <div style={styles.analyzerGapsList}>
              {activeGaps.map((item, idx) => (
                <div key={idx} style={styles.gapItemCard}>
                  <div style={styles.gapLeftCol}>
                    {item.missing ? (
                      <FaTimesCircle style={styles.missingIcon} />
                    ) : (
                      <FaCheckCircle style={styles.verifiedIcon} />
                    )}
                    <div style={styles.gapMeta}>
                      <span style={styles.gapItemName}>{item.name}</span>
                      <span style={styles.gapItemType}>{item.type}</span>
                    </div>
                  </div>

                  {item.missing ? (
                    <button 
                      onClick={() => handleStartSyllabus(item.name)}
                      style={styles.learnGapBtn}
                    >
                      <FaBookOpen style={{ marginRight: '6px' }} /> Learn Missing Skill
                    </button>
                  ) : (
                    <span style={styles.verifiedTextTag}>Verified Depth</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Insider referral Network */}
          <div style={styles.analyzerSection}>
            <div style={styles.referralHeaderRow}>
              <FaNetworkWired style={styles.referralIcon} />
              <h4 style={styles.sectionHeading}>Internal referral Network</h4>
            </div>
            <p style={styles.sectionDesc}>Meet these developers currently verified at {selectedJob.company} to ask for insight or referral recommendations:</p>

            <div style={styles.insidersGrid}>
              {insiders.map((insider, index) => (
                <div key={index} style={styles.insiderCard}>
                  <div style={styles.insiderProfile}>
                    <div style={styles.insiderAvatar}>{insider.avatar}</div>
                    <div style={styles.insiderMeta}>
                      <span style={styles.insiderName}>{insider.name}</span>
                      <span style={styles.insiderRole}>{insider.role}</span>
                    </div>
                  </div>
                  <button 
                    onClick={() => alert(`Opening referral message sequence to @${insider.handle}`)}
                    style={styles.referralMsgBtn}
                  >
                    Connect <FaChevronRight />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Apply with Consensus Live Resume */}
          <div style={styles.applicationFooterBox}>
            <button 
              onClick={() => alert(`Application bundle compiled! Your verified proof-of-work has been packaged and sent to hiring managers at ${selectedJob.company}.`)}
              style={styles.applyBtn}
            >
              Apply instantly using Verified Proof-of-Work <FaArrowRight style={{ marginLeft: '8px' }} />
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: '#0d1117',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  },
  jobsLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 1.2fr',
    gap: '24px',
    maxWidth: '1240px',
    margin: '0 auto'
  },
  jobsListPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  searchHeader: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '12px'
  },
  searchBox: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  searchIcon: {
    position: 'absolute',
    left: '12px',
    color: '#8b949e',
    fontSize: '14px'
  },
  jobSearchInput: {
    backgroundColor: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '8px 12px 8px 36px',
    color: '#c9d1d9',
    fontSize: '14px',
    width: '100%',
    outline: 'none'
  },
  cardsFeed: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  jobItemCard: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '16px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  activeJobCard: {
    borderColor: '#58a6ff',
    boxShadow: '0 0 0 1px #58a6ff'
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    position: 'relative'
  },
  companyIconPlaceholder: {
    width: '40px',
    height: '40px',
    borderRadius: '6px',
    backgroundColor: '#21262d',
    border: '1px solid #30363d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    color: '#8b949e',
    flexShrink: 0
  },
  jobMainMeta: {
    display: 'flex',
    flexDirection: 'column'
  },
  jobTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#f0f6fc',
    margin: 0
  },
  companyName: {
    fontSize: '12px',
    color: '#8b949e',
    marginTop: '2px'
  },
  matchPill: {
    position: 'absolute',
    right: 0,
    fontSize: '12px',
    fontWeight: '600',
    padding: '4px 8px',
    borderRadius: '4px'
  },
  cardSubDetails: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: '#8b949e'
  },
  subDetailText: {
    color: '#8b949e'
  },
  dotDivider: {
    color: '#30363d'
  },
  timeBadge: {
    color: '#8b949e',
    backgroundColor: '#21262d',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '11px'
  },
  gapAnalyzerPanel: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  panelTitleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderBottom: '1px solid #21262d',
    paddingBottom: '12px'
  },
  analyzerIcon: {
    color: '#8b949e',
    fontSize: '16px'
  },
  analyzerHeading: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#f0f6fc',
    margin: 0
  },
  targetJobCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  analyzerTargetCompany: {
    fontSize: '13px',
    color: '#58a6ff',
    fontWeight: '500'
  },
  analyzerTargetTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#f0f6fc',
    margin: 0
  },
  analyzerSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  sectionHeading: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#f0f6fc',
    margin: 0
  },
  sectionDesc: {
    margin: 0,
    fontSize: '12px',
    color: '#8b949e',
    lineHeight: '1.4'
  },
  analyzerGapsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    backgroundColor: '#0d1117',
    border: '1px solid #21262d',
    borderRadius: '6px',
    padding: '12px'
  },
  gapItemCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 8px',
    borderBottom: '1px solid #21262d'
  },
  gapLeftCol: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  missingIcon: {
    color: '#f85149',
    fontSize: '14px',
    flexShrink: 0
  },
  verifiedIcon: {
    color: '#39d353',
    fontSize: '14px',
    flexShrink: 0
  },
  gapMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  gapItemName: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#c9d1d9'
  },
  gapItemType: {
    fontSize: '11px',
    color: '#8b949e'
  },
  learnGapBtn: {
    backgroundColor: '#21262d',
    border: '1px solid #30363d',
    borderRadius: '4px',
    color: '#58a6ff',
    padding: '6px 12px',
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.2s'
  },
  verifiedTextTag: {
    fontSize: '11px',
    color: '#39d353',
    backgroundColor: 'rgba(35, 134, 54, 0.05)',
    padding: '4px 8px',
    borderRadius: '4px',
    fontWeight: '600'
  },
  referralHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  referralIcon: {
    color: '#8b949e',
    fontSize: '14px'
  },
  insidersGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  insiderCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#0d1117',
    border: '1px solid #21262d',
    borderRadius: '6px',
    padding: '12px'
  },
  insiderProfile: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  insiderAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#30363d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#f0f6fc',
    flexShrink: 0
  },
  insiderMeta: {
    display: 'flex',
    flexDirection: 'column'
  },
  insiderName: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#c9d1d9'
  },
  insiderRole: {
    fontSize: '11px',
    color: '#8b949e'
  },
  referralMsgBtn: {
    background: 'none',
    border: 'none',
    color: '#58a6ff',
    fontSize: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px'
  },
  applicationFooterBox: {
    borderTop: '1px solid #21262d',
    paddingTop: '16px',
    marginTop: '8px'
  },
  applyBtn: {
    backgroundColor: '#238636',
    border: '1px solid #2ea44f',
    borderRadius: '6px',
    color: '#ffffff',
    padding: '12px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.15s'
  }
};